/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef } from 'react'
import { handleMediaQuery, initializeAnalytics, trackEvent } from '@offerzen/pinata-shame'
import PropTypes from 'propTypes'
import { Route, Switch, withRouter } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'

import HomeScene from './scenes/HomeScene'
import NewMemberRegistrationMain from '@/NewMemberRegistration/Main'
import { StaticDataProvider } from './contexts/StaticDataContext'

import Body from './components/Body'
import ScrollTop from './components/ScrollTop'
import Navbar, { navbarHeight } from './compounds/Navbar'
import CurrentCompany from './containers/CurrentCompany'
import CurrentUser from './containers/CurrentUser'
import SkeletonCompanyExperience from './skeletons/SkeletonCompanyExperience'
import PopulateInitialState from './components/PopulateInitialState'
import SystemErrorRedux from './components/SystemErrorRedux'
import AdminPanel from './components/AdminPanel'
import ToastProvider from './components/Toast/ToastProvider'
import { identifyEmployer } from './helpers/segmentHelpers'
import NestedRoutes from './NestedRoutes'

// Import main styles for the full app, might change when we do clara SPA
import styles from './styles'

const CompanyExperienceMain = ({
  companyDetails,
  user,
  location,
  isBreakpointOrLarger,
  isDesktopOrLarger,
  isPhoneOrLarger,
  isTabletOrLarger,
  productPlan,
}) => {
  const mediaProps = {
    isBreakpointOrLarger,
    isDesktopOrLarger,
    isPhoneOrLarger,
    isTabletOrLarger,
  }

  const analyticsInitialised = useRef(false)
  useEffect(() => {
    if (!analyticsInitialised.current) {
      initializeAnalytics(process.env.REACT_APP_SEGMENT_WRITE_KEY || 'test')
      analyticsInitialised.current = true
    }
  }, [analyticsInitialised])

  const identifyEmployerCalled = useRef(false)
  useEffect(() => {
    if (user && user.id && !isEmpty(companyDetails) && !identifyEmployerCalled.current) {
      identifyEmployer(user, companyDetails, productPlan)
      identifyEmployerCalled.current = true
    }
  }, [user, companyDetails, productPlan])

  const { pathname } = location
  useEffect(() => {
    if (analyticsInitialised.current) {
      trackEvent('Company: pathname action', { action: 'changed pathname', source: 'spa', pathname })
    }
  }, [pathname, analyticsInitialised])

  return (
    <Switch>
      <Route exact path="/">
        <HomeScene />
      </Route>

      <Route exact path="/member/signup" render={routeProps => <NewMemberRegistrationMain {...routeProps} />} />

      {/* Pick up any route to render the app and inherit the router props */}
      <Route
        path="/company/:companySlug"
        render={rootRouteProps => (
          <PopulateInitialState companyId={rootRouteProps.match.params.companySlug} searchParams={rootRouteProps.location.search}>
            {({ initialState, refetchInitialState }) => (
              <StaticDataProvider value={initialState.static_data}>
                <CurrentCompany companySlug={rootRouteProps.match.params.companySlug}>
                  {companyProps => (
                    <CurrentUser>
                      {userProps => (
                        <ToastProvider>
                          <Navbar {...mediaProps} {...rootRouteProps} {...userProps} {...companyProps} />
                          <Body>
                            <ScrollTop location={rootRouteProps.location}>
                              <SystemErrorRedux offsetTop={navbarHeight} className={styles.systemError} />

                              <Choose>
                                <When condition={companyProps.loadingCompany}>
                                  <SkeletonCompanyExperience />
                                </When>
                                <Otherwise>
                                  <NestedRoutes
                                    companyDetails={companyDetails}
                                    companyProps={companyProps}
                                    rootRouteProps={rootRouteProps}
                                    mediaProps={mediaProps}
                                    userProps={userProps}
                                    refetchInitialState={refetchInitialState}
                                  />
                                </Otherwise>
                              </Choose>
                            </ScrollTop>
                            <If condition={userProps.category === 'admin'}>
                              <AdminPanel companyId={companyProps.companyId} hubspotUrl={userProps.hubspotUrl} />
                            </If>
                          </Body>
                        </ToastProvider>
                      )}
                    </CurrentUser>
                  )}
                </CurrentCompany>
              </StaticDataProvider>
            )}
          </PopulateInitialState>
        )}
      />
    </Switch>
  )
}

const { bool, shape, string } = PropTypes
CompanyExperienceMain.propTypes = {
  user: shape({
    email: string,
    name: string,
    id: string,
  }),
  companyDetails: shape({
    id: string,
    name: string,
    created_at: string,
  }),
  isBreakpointOrLarger: shape({}),
  isDesktopOrLarger: bool,
  isPhoneOrLarger: bool,
  isTabletOrLarger: bool,
  location: shape({
    pathname: string,
  }),
}

export default handleMediaQuery()(withRouter(CompanyExperienceMain))
